'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["autoFocus", "className", "disabled", "error", "format", "iconSize", "id", "Input", "inputRef", "inputWidth", "interval", "max", "min", "name", "onBlur", "onChange", "onFocus", "onKeyDown", "onOpenChange", "open", "readOnly", "use", "value"];
import PropTypes from 'prop-types';
import { Component } from 'react';
import I18n from 'I18n';
import invariant from 'react-utils/invariant';
import memoize from 'react-utils/memoize';
import { callIfPossible } from '../core/Functions';
import SyntheticEvent from '../core/SyntheticEvent';
import Controllable from '../decorators/Controllable';
import ShareInput from '../decorators/ShareInput';
import { FieldsetContextConsumer } from '../context/FieldsetContext';
import UIControlledPopover from '../tooltip/UIControlledPopover';
import refObject from '../utils/propTypes/refObject';
import { hidden } from '../utils/propTypes/decorators';
import { uniqueId } from '../utils/underscore';
import { formatValue, parseValue, PrivateTimePickerOptions } from './internal/UITimeInputInternals';
import UIInputIconWrapper from './internal/UIInputIconWrapper';
import UINumberInput from './UINumberInput';
import { listAttrIncludes } from '../utils/Dom';
import { jsx as _jsx } from "react/jsx-runtime";
const MAX_TIME_OF_DAY = 1439; // 24 * 60 - 1

const roundToNearest = (value, step, round = Math.round) => round(value / step) * step;
const getLocalizedTimeFormat = memoize(() => 'HH:MM'.replace(/HH/, I18n.text('ui.timePicker.placeholder.hour')).replace(/MM/, I18n.text('ui.timePicker.placeholder.minute')));
class UITimeInput extends Component {
  static getDerivedStateFromProps(props, state) {
    const isOpening = !state.prevOpen && props.open;
    if (isOpening) {
      return {
        activeDescendant: props.value,
        prevOpen: props.open
      };
    }
    if (state.prevOpen !== props.open) {
      return {
        prevOpen: props.open
      };
    }
    return null;
  }
  constructor(props) {
    super(props);
    this.numberInputFormatter = value => {
      return formatValue(this.props.format, value);
    };
    this.numberInputParser = rawValue => {
      return parseValue(this.props.format, rawValue);
    };
    this.makeListboxOptionId = value => {
      return `${this._listboxOptionIdPrefix}-${value}`;
    };
    this.handleOptionClick = evt => {
      callIfPossible(this.props.onChange, SyntheticEvent(evt.target.value));
    };
    this.handleOptionHover = value => {
      this.setActiveDescendant(value);
    };
    this.handleInputFocus = evt => {
      const {
        inputRef,
        onFocus,
        onOpenChange
      } = this.props;
      onOpenChange(SyntheticEvent(true));
      inputRef.current.select();
      if (onFocus) onFocus(evt);
    };
    this.handleInputBlur = evt => {
      callIfPossible(this.props.onOpenChange, SyntheticEvent(false));
      callIfPossible(this.props.onBlur, evt);
    };
    this.handleInputChange = evt => {
      this.setActiveDescendant(evt.target.value);
      callIfPossible(this.props.onChange, evt);
    };
    this.handleInputKeyDown = evt => {
      const {
        value,
        interval,
        min,
        max,
        onChange,
        open,
        onOpenChange,
        onKeyDown,
        disabled,
        readOnly
      } = this.props;
      if (disabled || readOnly) {
        return;
      }
      const {
        activeDescendant
      } = this.state;
      const firstAvailableOption = min;
      const lastAvailableOption = max;
      let newValue = value;
      let newOpen = open;
      let newActiveDescendant = activeDescendant;
      switch (evt.key) {
        case 'ArrowDown':
          evt.preventDefault(); // Don't move the cursor
          newOpen = true;
          // Increment or default to first option in list
          newActiveDescendant = activeDescendant == null ? firstAvailableOption : Math.min(lastAvailableOption, activeDescendant + interval);
          newActiveDescendant = roundToNearest(newActiveDescendant, interval, Math.floor);
          break;
        case 'ArrowUp':
          evt.preventDefault(); // Don't move the cursor
          newOpen = true;
          // Decrement or default to last option in list
          newActiveDescendant = activeDescendant == null ? lastAvailableOption : Math.max(firstAvailableOption, activeDescendant - interval);
          newActiveDescendant = roundToNearest(newActiveDescendant, interval, Math.ceil);
          break;
        case 'Enter':
          // Set activeDescendant as new value on selection
          if (open) {
            newValue = activeDescendant;
          }
          newOpen = !open;
          break;
        default:
          callIfPossible(onKeyDown, evt);
          return;
      }
      if (activeDescendant !== newActiveDescendant) {
        this.setActiveDescendant(newActiveDescendant);
      }
      if (value !== newValue) {
        callIfPossible(onChange, SyntheticEvent(newValue));
      }
      if (open !== newOpen) {
        callIfPossible(onOpenChange, SyntheticEvent(newOpen));
      }
    };
    this.state = {
      activeDescendant: props.value,
      popoverWidth: 'auto',
      prevOpen: props.open
    };
    this._listboxOptionIdPrefix = `UITimeInput-${uniqueId()}-result-item`;
  }
  componentDidMount() {
    this.updatePopoverContentWidth();
  }
  componentDidUpdate(prevProps) {
    const isOpening = !prevProps.open && this.props.open;
    if (isOpening) {
      this.updatePopoverContentWidth();
    }
  }

  /** @private */

  /** @private */

  setActiveDescendant(value) {
    this.setState({
      activeDescendant: value
    });
  }
  updatePopoverContentWidth() {
    const inputEl = this.props.inputRef.current;
    if (inputEl) {
      const {
        width: exactWidth
      } = inputEl.getBoundingClientRect();
      const roundedWidth = window.chrome ? Math.floor(exactWidth) : exactWidth;
      this.setState({
        popoverWidth: roundedWidth // account for 1px border added by UIPopover
      });
    }
  }
  render() {
    const _this$props = this.props,
      {
        autoFocus,
        className,
        disabled,
        error,
        iconSize,
        id,
        Input,
        inputRef,
        inputWidth,
        interval,
        max,
        min,
        name,
        onOpenChange: onPopoverOpenChange,
        open: popoverOpen,
        readOnly,
        use,
        value: normalizedValue
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      activeDescendant
    } = this.state;
    if (process.env.NODE_ENV !== 'production') {
      invariant(min >= 0, 'UITimeInput: `min` should be a positive integer (received %s)', min);
      invariant(max >= 0, 'UITimeInput: `max` should be a positive integer (received %s)', max);
      invariant(max <= MAX_TIME_OF_DAY, 'UITimeInput: `max` should not be greater than 11:59 PM (received %s > %s)', max, MAX_TIME_OF_DAY);
      invariant(min < max, 'UITimeInput: `min` should not be greater than `max` (received %s > %s)', min, max);
    }
    const computedPopoverOpen = popoverOpen && !disabled && !readOnly;
    return /*#__PURE__*/_jsx(FieldsetContextConsumer, {
      children: fieldsetContext => {
        const computedDisabled = disabled || fieldsetContext.disabled;
        return /*#__PURE__*/_jsx(UIControlledPopover, {
          arrowSize: "none",
          placement: "bottom",
          width: this.state.popoverWidth,
          open: computedPopoverOpen,
          onOpenChange: onPopoverOpenChange,
          content: /*#__PURE__*/_jsx(PrivateTimePickerOptions, {
            focusedValue: activeDescendant,
            interval: interval,
            makeOptionId: this.makeListboxOptionId,
            max: max,
            min: min,
            onOptionClick: this.handleOptionClick,
            onOptionHover: this.handleOptionHover,
            valueFormatter: this.numberInputFormatter
          }),
          children: /*#__PURE__*/_jsx(UIInputIconWrapper, Object.assign({}, rest, {
            className: className,
            disabled: computedDisabled,
            iconName: "time",
            size: iconSize || fieldsetContext.size,
            children: /*#__PURE__*/_jsx(Input, {
              "aria-activedescendant": activeDescendant ? this.makeListboxOptionId(activeDescendant) : undefined,
              className: listAttrIncludes(className, 'private-form__control--inline') ? 'private-form__control--inline' : undefined,
              autoComplete: "off",
              autoFocus: autoFocus,
              disabled: computedDisabled,
              error: error,
              formatter: this.numberInputFormatter,
              id: id,
              inputRef: inputRef,
              max: max,
              min: min,
              name: name,
              onBlur: this.handleInputBlur,
              onChange: this.handleInputChange,
              onFocus: this.handleInputFocus,
              onKeyDown: this.handleInputKeyDown,
              parser: this.numberInputParser,
              placeholder: getLocalizedTimeFormat(),
              readOnly: readOnly,
              use: use,
              value: normalizedValue,
              width: inputWidth
            })
          }))
        });
      }
    });
  }
}
UITimeInput.displayName = 'UITimeInput';
UITimeInput.defaultProps = {
  min: 0,
  max: MAX_TIME_OF_DAY,
  format: 'LT',
  Input: UINumberInput,
  use: 'default',
  interval: 30,
  open: false
};
UITimeInput.propTypes = {
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  format: PropTypes.string.isRequired,
  iconSize: hidden(PropTypes.number),
  Input: hidden(PropTypes.elementType),
  inputRef: refObject,
  inputWidth: hidden(PropTypes.number),
  interval: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onOpenChange: PropTypes.func,
  open: PropTypes.bool,
  readOnly: PropTypes.bool,
  use: PropTypes.oneOf(['default', 'on-dark']).isRequired,
  value: PropTypes.number
};
export default ShareInput(Controllable(UITimeInput, ['open', 'value']));